/**
 * Через какую кассу идут рубли — решает бэкенд (backend/rub_provider.py,
 * GET /api/payments/rub-provider).
 *
 * астрофил.рф: всегда ЮKassa, сборка и так собрана под неё — запроса нет.
 * astrofeel.ru: сборка под Робокассу (REACT_APP_PAYMENT_PROVIDER=robokassa),
 * но когда на бэкенде ру включена ЮKassa магазина 1335266, рубли идут через
 * неё — без пересборки фронта. Пока ответа нет или он не пришёл — Робокасса,
 * как было.
 *
 * IS_ROBOKASSA из siteConfig по-прежнему значит «сборка ру» (Stripe первым у
 * иностранцев, разовые покупки, акции и т.п.). Касса рублей — только здесь.
 */
import { useEffect, useState } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { IS_ROBOKASSA, DOUBLE_BENEFIT_ENABLED } from '@/lib/siteConfig';
import { STRIPE_PRIMARY } from '@/lib/visitorRegion';

export const YOOKASSA = 'yookassa';
export const ROBOKASSA = 'robokassa';

const state = {
  provider: IS_ROBOKASSA ? ROBOKASSA : YOOKASSA,
  // На рф спрашивать нечего — ответ известен сразу.
  ready: !IS_ROBOKASSA,
};
const listeners = new Set();
let pending = null;

function notify() {
  listeners.forEach((fn) => fn({ ...state }));
}

/** Узнать кассу у бэкенда (один раз). Ошибка — остаёмся на Робокассе. */
export function loadRubProvider(fetchImpl = (typeof fetch !== 'undefined' ? fetch : null)) {
  if (state.ready) return Promise.resolve(state.provider);
  if (pending) return pending;
  if (!fetchImpl) {
    state.ready = true;
    return Promise.resolve(state.provider);
  }
  pending = fetchImpl(`${BACKEND_URL}/api/payments/rub-provider`)
    .then((r) => (r && r.ok ? r.json() : null))
    .then((data) => {
      if (data && (data.provider === YOOKASSA || data.provider === ROBOKASSA)) {
        state.provider = data.provider;
      }
    })
    .catch(() => {})
    .then(() => {
      state.ready = true;
      notify();
      return state.provider;
    });
  return pending;
}

/** Касса рублей сейчас (синхронно; до ответа бэкенда на ру — Робокасса). */
export function getRubProvider() {
  return state.provider;
}

/** Дождаться ответа и вернуть кассу — перед созданием платежа. */
export function ensureRubProvider() {
  return loadRubProvider();
}

/** Для компонентов: { provider, ready, isRobokassa }. */
export function useRubProvider() {
  const [snap, setSnap] = useState({ ...state });
  useEffect(() => {
    listeners.add(setSnap);
    loadRubProvider();
    setSnap({ ...state });
    return () => { listeners.delete(setSnap); };
  }, []);
  return { provider: snap.provider, ready: snap.ready, isRobokassa: snap.provider === ROBOKASSA };
}

/**
 * Рубли идут через Робокассу — и это ПОДТВЕРДИЛ бэкенд (откат RUB_PROVIDER=
 * robokassa). Для правил показа (разовые 199 ₽, «Двойная выгода»): пока ответа
 * нет, считаем, что касса ЮKassa — так на ру нет мигания кнопок у всех ради
 * редкого отката. Сама оплата всё равно ждёт ответа (ensureRubProvider).
 */
export function rubIsRobokassa() {
  return state.ready && state.provider === ROBOKASSA;
}

/**
 * «ДВОЙНАЯ ВЫГОДА» и зачёт разовых в первый месяц ПРО — рублёвый путь на
 * обоих сайтах (решение владелицы 30.10.2026, отменяет 01.10.2026): касса
 * ЮKassa, посетитель не на Stripe-пути (иностранцу на ру — Stripe, там акции
 * пока нет). Сервер право пересчитывает сам (backend/double_benefit.py).
 */
export function doubleBenefitOn(stripePrimary = STRIPE_PRIMARY) {
  return !!DOUBLE_BENEFIT_ENABLED && !stripePrimary && !rubIsRobokassa();
}

/** Только для тестов. */
export function _resetRubProviderForTests(provider = IS_ROBOKASSA ? ROBOKASSA : YOOKASSA, ready = !IS_ROBOKASSA) {
  state.provider = provider;
  state.ready = ready;
  pending = null;
  listeners.clear();
}
